import React, { forwardRef } from 'react';

import { useRecoilValue } from 'recoil';

import { ia, iaRa, inRn } from 'lib/helpers/utility';
import { COMPLETED_TRANSACTION_STATUSES } from 'lib/constants/transaction/completedTransactionStatuses';

import { userState as state } from 'components/state';
import DocumentPages from 'components/shared/DocumentSheet/DocumentPages';
import DocFooter from 'components/shared/DocumentSheet/components/DocFooter';
import DocHeader from 'components/shared/DocumentSheet/components/DocHeader';
import { buildDocLegal } from 'components/shared/DocumentSheet/lib/docLegal';
import DocCompactHeader from 'components/shared/DocumentSheet/components/DocCompactHeader';

import Loader from '../Loader/Loader';
import { currentPractice } from '../../practice/practiceState';

import { toReceiptPractice } from './lib/receiptPractice';
import { buildReceiptBlocks } from './lib/buildReceiptBlocks';
import { buildDetailEntries, buildDocLines } from './lib/buildReceiptDetails';
import { useReceiptItemsBlock } from './components/ReceiptItems/lib/useReceiptItemsBlock';

const Receipt = forwardRef(({ receipt, walkout = false, isPatient: isPatientProp }, ref) => {
  const patient = receipt?.patient;
  const payer = receipt?.payer;
  const transaction = receipt?.transaction;
  const transactions = receipt?.transactions;

  let practice = useRecoilValue(currentPractice);
  const userState = useRecoilValue(state);

  const isPatient =
    isPatientProp ?? (!userState.logged_in || (userState?.logged_in && !practice?.id));

  if (!practice.id) practice = receipt?.practice;

  practice = toReceiptPractice(practice);

  const timezone = practice?.timezone;
  const currentTransactions = ia(receipt?.transactions) ? receipt?.transactions : [transaction];

  const { block: itemsBlock, canSeeAmounts } = useReceiptItemsBlock({
    transactions: currentTransactions,
    isPatient,
    timezone,
    provider: receipt?.provider,
    payer
  });

  if (!practice)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader color="primary" size={40} />
      </div>
    );

  const walkoutTransactions = iaRa(receipt?.walkoutTransactions);
  const historyTransactions = walkout ? walkoutTransactions : [];

  const isCompleted = COMPLETED_TRANSACTION_STATUSES.includes(transaction?.status?.toLowerCase());
  const receiptNumber = receipt?.id ?? transaction?.id;

  const detailEntries = buildDetailEntries({
    patient,
    payer,
    transaction,
    transactions: currentTransactions,
    timezone
  });

  const docLines = buildDocLines({
    receiptId: receiptNumber,
    transaction,
    timezone,
    isCompleted,
    canSeeAmounts,
    amountPaid: inRn(transaction?.amount_paid_self)
  });

  const patientName = [patient?.f_name, patient?.l_name].filter(Boolean).join(' ') || payer?.name;

  const closing = practice?.phone
    ? `Retain for your records – suitable for HSA/FSA substantiation. Questions? Call ${practice.phone} within 60 days.`
    : 'Retain for your records – suitable for HSA/FSA substantiation.';

  const legal = buildDocLegal(practice, 'Receipt of payment – retain for your records.');

  const blocks = buildReceiptBlocks({
    receipt,
    practice,
    patient,
    transaction,
    transactions,
    detailEntries,
    itemsBlock,
    historyTransactions,
    walkout,
    walkoutTransactions,
    canSeeAmounts,
    isPatient,
    timezone,
    closing
  });

  return (
    <div
      ref={ref}
      data-receipt-id={transaction?.id}
      className="flex flex-1 items-start justify-center overflow-auto bg-neutral-100 py-6 print:overflow-visible print:bg-white print:py-0">
      <DocumentPages
        key={transaction?.id}
        blocks={blocks}
        renderHeader={(pageIndex) =>
          pageIndex === 0 ? (
            <DocHeader
              practice={practice}
              title="Receipt"
              kicker="Payment confirmation"
              lines={docLines}
            />
          ) : (
            <DocCompactHeader
              practice={practice}
              reference={receiptNumber ? `Receipt #${receiptNumber}` : 'Receipt'}
              meta={[patientName].filter(Boolean).join(' · ')}
            />
          )
        }
        renderFooter={({ page, pageCount }) => (
          <DocFooter legal={legal} page={`Page ${page} of ${pageCount}`} />
        )}
      />
    </div>
  );
});

export default Receipt;
